// react
import { ReactNode, useContext, useMemo, useState } from 'react';

// types
import { ActionButton, Entity, Frame, Pagination, User, UserPayload } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Alert, Button, Grid } from '@mui/material';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardUser } from '../../../cards/CardUser/CardUser';

// dialogs
import { DialogOnboardUsers } from '../../../dialogs/DialogOnboardUsers/DialogOnboardUsers';

// ui
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// icons
import { PersonOutlined as IconPerson } from '@mui/icons-material';

// props
export interface TableUsersProps {
	entity?: Entity | null;
	frame?: Frame;
	isLoading: boolean;
	pagination?: Pagination;
	refetchUsers?: () => void;
	users: User[];
}

export const TableUsers = ({
	entity,
	frame,
	isLoading,
	pagination,
	refetchUsers,
	users,
}: TableUsersProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramQ = searchParams.get('q');

	// state
	const [usersWbg, setUsersWbg] = useState<UserPayload[]>([]);

	// fetch WBG users not yet onboarded
	useFetch({
		isEnabled: Boolean(entity?.idWbg),
		url: `${process.env.REACT_APP_API_URL}/wbg/organizations/${entity?.idWbg}/users?isOnboarded=true`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsersWbg(res.data);
			}
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			isSortable: true,
			label: t('name'),
			value: 'firstName',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('jobTitle'),
			value: 'title',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('email'),
			value: 'email',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('phone'),
			value: 'phone',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: 2.5,
			},
			isSortable: true,
			label: t('lastUpdated'),
			value: 'modified',
		},
	];

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.users.content') },
			IconCircleProps: { icon: (<IconPerson />) as ReactNode },
			isLoading,
			title: { children: t('empty.users.title') },
		};

		if (userProjectPermissions?.user?.create && emptyProps.actions) {
			emptyProps.actions.push({
				children: t('addUser'),
				onClick: () => {
					searchParams.set('dialog', 'addUser');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramQ) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [isLoading, paramQ, searchParams, setSearchParams, t, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !users.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{users.map((user, i) => {
					return (
						<Grid item key={`user-${i}`} xs={12}>
							<CardUser frame={frame} refetchUsers={refetchUsers} user={user} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, isLoading, refetchUsers, users]);

	return (
		<Grid container spacing={2}>
			{userProjectPermissions?.user?.create && usersWbg.length > 0 && (
				<Grid item xs={12}>
					<Alert
						action={
							<Button
								color="primary"
								onClick={() => {
									searchParams.set('dialog', 'onboardUsers');
									setSearchParams(searchParams);
								}}
								variant="contained">
								{t('onboardUsers')}
							</Button>
						}
						severity="info"
						sx={{ alignItems: 'center', paddingRight: 2.5 }}
						variant="outlined">
						{t('alert.usersNotOnboarded', { count: usersWbg.length })}
					</Alert>
				</Grid>
			)}
			<Grid item xs={12}>
				<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
			</Grid>
			<Grid item xs={12}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			{pagination && (
				<Grid item xs={12}>
					<TableFooter numShowing={users.length} pagination={pagination} />
				</Grid>
			)}
			{entity && (
				<DialogOnboardUsers
					entity={entity}
					isOpen={paramDialog === 'onboardUsers'}
					onClose={() => {
						searchParams.delete('dialog');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						setUsersWbg([]);
						if (refetchUsers) {
							refetchUsers();
						}
					}}
					usersWbg={usersWbg}
				/>
			)}
		</Grid>
	);
};
